/**
 * SDK Configuration Utilities
 * Handles building configuration objects for the Prizeout SDK
 */

// SDK type declarations
declare global {
    interface Window {
        prizeoutSDK?: {
            init: (config: SDKInitConfig) => void;
            close?: () => void;
        };
        _csconf?: {
            env?: string;
            conf?: {
                country?: string;
                default_region?: string;
            };
        };
        __sdkInitialized?: boolean;
    }
}

export interface SDKCallbacks {
    onInit: (payload?: unknown) => void;
    onClose: (payload?: unknown) => void;
    onCashoutFail: (payload?: unknown) => void;
    onCashoutSuccess: (payload: CashoutPayload) => void;
}

export interface SDKInitConfig {
    env: string;
    localhost: boolean;
    partner_id?: string;
    session_id?: string;
    user?: Record<string, unknown>;
    publisher?: Record<string, unknown>;
    forceV2: boolean;
    isNestedIFrame?: boolean;
    themeOverride?: string;
    callbacks: SDKCallbacks;
}

export interface CashoutPayload {
    giftcard: {
        cost_in_cents: number;
    };
}

export interface SDKEnvironmentConfig {
    env: string;
    country: string;
    region: string;
    isLocalhost: boolean;
}

export interface SDKSettings {
    user?: {
        session_id?: string;
        balance?: number;
        [key: string]: unknown;
    };
    publisher?: {
        id?: string;
        [key: string]: unknown;
    };
    partner_id?: string;
    session_id?: string;
}

/**
 * Get environment configuration for SDK
 */
export const getSDKEnvironmentConfig = (): SDKEnvironmentConfig => ({
    env: window._csconf?.env || process.env.PRIZEOUT_ENV || 'dev',
    country: window._csconf?.conf?.country || 'US',
    region: window._csconf?.conf?.default_region || 'default',
    isLocalhost: window.location.hostname === 'localhost',
});

/**
 * Build SDK configuration from settings and environment
 */
export const buildSDKConfig = (
    settings: SDKSettings,
    envConfig: SDKEnvironmentConfig,
): Omit<SDKInitConfig, 'callbacks'> => {
    // Ensure balance is always a number (SDK requires it)
    const rawBalance = typeof settings.user?.balance === 'number' ? settings.user.balance : 0;
    const userWithBalance = {
        ...settings.user,
        balance: rawBalance,
    };

    // Build theme override URL for fullscreen mode - whitelabeled for localhost
    const themeOverrideUrl = envConfig.isLocalhost ? `${window.location.origin}/widget-theme-local.css` : undefined;

    return {
        env: envConfig.env,
        localhost: envConfig.isLocalhost,
        partner_id: settings.partner_id,
        session_id: settings.session_id,
        user: settings.user ? userWithBalance : undefined,
        publisher: settings.publisher,
        forceV2: true,
        isNestedIFrame: true,
        themeOverride: themeOverrideUrl,
    };
};

/**
 * Check if SDK is available on window
 */
export const isSDKAvailable = (): boolean => !!window.prizeoutSDK;

/**
 * Check if SDK is already initialized
 */
export const isSDKInitialized = (): boolean => !!window.__sdkInitialized;

/**
 * Set SDK initialized flag
 */
export const setSDKInitializedFlag = (value: boolean): void => {
    window.__sdkInitialized = value;
};
